// @givmo/board — a copy of the return provided to the board, and its receipts (CBG-YR5-DIST's web
// half and CBG-YR5-REVIEW; frame p7; the backend's `board-return-distribution.md` §8.3 and
// `board-return-review.md` §4.1–§6).
//
// The headline says when the copy reached every voting director recorded at the act (P2: the read's
// own day, `directors_provided_through`), the email line says who was emailed when, naming only the
// exceptions (P4), and each person's row says what Board recorded of them: their first opening
// through the director door, a printed copy handed over, a recorded reading of these exact bytes.
// Beside them, the copy with its whole fingerprint, and the board's review, linked.
//
// ⚠ "Emailed" says Board handed the mail to the mail service, never that anyone read it, and
// "Opened" is the person's own first fetch of the copy through their own account, never an email
// open. Nothing here says line 11a is met, and nothing reads the return.
//
// The quiet acts (a printed copy, Email again, a newer copy, withdrawing) are the roster's grant act
// (`board_member:grant_access`); the review's link and removal are the in-meeting record's string
// (`board_meeting:write`). A control the reader can't use is absent, never disabled with a note.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, CardBody, Skeleton, toast } from '@givmo/design-system';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { Link, useParams } from '../../board-routing';
import { Fingerprint } from '../../components/fingerprint';
import { PageShell } from '../../components/page-shell';
import { displayLongDate } from '../../lib/display';
import { oneLine, shortDay } from '../conflicts/coi-decode';
import { PartLabel } from '../conflicts/coi-parts';
import { ProvideDialog } from './provide-dialog';
import { PrintedCopyDialog, WithdrawCopyDialog } from './receipt-dialogs';
import {
  decodeCopy,
  emailLines,
  fileSize,
  headline,
  personFacts,
  personName,
  readCell,
  type ReturnCopy,
  type ReturnPerson,
} from './return-decode';
import {
  RETURN_ACT_PERMISSION,
  REVIEW_LINK_PERMISSION,
  servedSentence,
  useReturnCopy,
  useSettleReturnAct,
} from './return-queries';
import { returnWords as w } from './return-words';
import { ReviewCard } from './review-card';

const PLAIN = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const STRONG = 'font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong';
const COLUMN = 'font-sans text-s12 font-w600 tracking-givmo text-text-tertiary';
const LINK =
  'font-sans text-s14 font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const QUIET_LINK =
  'self-start font-sans text-s14 font-w600 text-text-tertiary outline-none transition-colors hover:text-text-strong focus-visible:underline';

/** "Apr 1, 7:41 PM": an opening inside the copy's own story; its year only when it differs. */
function openedWords(iso: string, providedOn: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const sameYear = String(date.getFullYear()) === providedOn.slice(0, 4);
  return oneLine(
    date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
      hour: 'numeric',
      minute: '2-digit',
    }),
  );
}

export function ReturnReceiptsPage() {
  const { orgRef, isReady, permissions } = useBoardHost();
  const { recordId } = useParams({ from: '/authenticated/form-990/returns/$recordId' });
  const query = useReturnCopy(orgRef, recordId, isReady);
  const read = query.data;

  if (!isReady || orgRef === null || query.isPending) {
    return (
      <PageShell title={w.backToForm990} testId="page-return-receipts">
        <Skeleton className="h-[320px] w-full rounded-xl" testId="return-receipts-loading" />
      </PageShell>
    );
  }
  if (query.isError || read === undefined) {
    return (
      <PageShell title={w.backToForm990} testId="page-return-receipts">
        <Card testId="return-receipts-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className={PLAIN}>{w.loadFailed}</p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void query.refetch()}
              testId="return-receipts-retry"
            >
              {w.tryAgain}
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }
  if (read.kind === 'not-served') {
    return (
      <PageShell title={w.backToForm990} testId="page-return-receipts">
        <Card testId="return-receipts-not-served">
          <CardBody>
            <p className={PLAIN}>{w.notFound}</p>
          </CardBody>
        </Card>
      </PageShell>
    );
  }
  return (
    <Receipts
      orgRef={orgRef}
      copy={read.copy}
      canAct={hasBoardPermission(permissions, RETURN_ACT_PERMISSION)}
      canLink={hasBoardPermission(permissions, REVIEW_LINK_PERMISSION)}
    />
  );
}

type Opened = 'printed' | 'withdraw' | 'newer' | null;

function Receipts({
  orgRef,
  copy,
  canAct,
  canLink,
}: {
  orgRef: string;
  copy: ReturnCopy;
  canAct: boolean;
  canLink: boolean;
}) {
  const [opened, setOpened] = useState<Opened>(null);
  const withdrawn = copy.withdrawn !== null;
  const canRecordPrinted =
    canAct && !withdrawn && copy.people.some((person) => person.can_record_printed_copy);
  const said = headline(copy);
  const withdrawnDay =
    copy.withdrawn?.withdrawn_at === null || copy.withdrawn === null
      ? null
      : displayLongDate(copy.withdrawn.withdrawn_at);

  return (
    <PageShell
      title={w.receiptsTitle(copy.tax_year.named_year, copy.form)}
      description={
        said === null && !withdrawn ? undefined : (
          <span className="flex flex-col gap-2">
            {said !== null ? <span data-testid="return-receipts-headline">{said}</span> : null}
            {withdrawnDay !== null ? (
              <span data-testid="return-receipts-withdrawn">
                {w.withdrawnOn(oneLine(withdrawnDay))}
              </span>
            ) : null}
            {/* The officer's own reason, quoted as theirs, beside the line it explains. */}
            {copy.withdrawn !== null && copy.withdrawn.reason.length > 0 ? (
              <span className={QUIET} data-testid="return-withdrawn-reason">
                “{copy.withdrawn.reason}”
              </span>
            ) : null}
          </span>
        )
      }
      actions={
        canRecordPrinted ? (
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => setOpened('printed')}
            testId="return-record-printed-copy"
          >
            {w.recordPrintedCopy}
          </Button>
        ) : undefined
      }
      testId="page-return-receipts"
    >
      <div className="grid grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_320px]">
        <PeopleCard orgRef={orgRef} copy={copy} canAct={canAct && !withdrawn} />
        <div className="flex flex-col gap-16">
          <CopyCard orgRef={orgRef} copy={copy} />
          <ReviewCard orgRef={orgRef} copy={copy} canLink={canLink} />
          <QuietActs
            copy={copy}
            canAct={canAct}
            onNewer={() => setOpened('newer')}
            onWithdraw={() => setOpened('withdraw')}
          />
        </div>
      </div>
      {opened === 'printed' ? (
        <PrintedCopyDialog orgRef={orgRef} copy={copy} onClose={() => setOpened(null)} />
      ) : null}
      {opened === 'withdraw' ? (
        <WithdrawCopyDialog orgRef={orgRef} copy={copy} onClose={() => setOpened(null)} />
      ) : null}
      {opened === 'newer' ? (
        <ProvideDialog
          orgRef={orgRef}
          target={{
            taxYear: copy.tax_year,
            form: copy.form,
            // The copy's own last day: with no year end recorded, the year's day is its end's.
            yearEnd: {
              month: Number(copy.tax_year.ends_on.slice(5, 7)),
              day: Number(copy.tax_year.ends_on.slice(8, 10)),
            },
            followsRecordId: copy.record_id,
            instructions: null,
          }}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </PageShell>
  );
}

// ── The people (frame p7's main card) ────────────────────────────────────────────────────────────

function PeopleCard({
  orgRef,
  copy,
  canAct,
}: {
  orgRef: string;
  copy: ReturnCopy;
  canAct: boolean;
}) {
  const lines = emailLines(copy);
  return (
    <Card testId="return-people">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {w.people}
          </h2>
          {lines.lead !== null ? (
            <p className={QUIET} data-testid="return-email-line">
              {lines.lead}
            </p>
          ) : null}
          {lines.exceptions.length > 0 ? (
            <ul className="flex flex-col gap-2" data-testid="return-email-exceptions">
              {lines.exceptions.map((line, index) => (
                <li key={index} className={QUIET}>
                  {line}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* Wide: the frame's three columns. */}
        <table
          className="hidden w-full table-fixed border-collapse md:table"
          data-testid="return-people-table"
        >
          <colgroup>
            <col style={{ width: '44%' }} />
            <col style={{ width: '33%' }} />
            <col style={{ width: '23%' }} />
          </colgroup>
          <thead>
            <tr>
              {[w.colPerson, w.colOpened, w.colRecordedReading].map((column) => (
                <th
                  key={column}
                  scope="col"
                  className={`border-b border-border-subtle pb-8 pr-16 text-left ${COLUMN}`}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {copy.people.map((person) => (
              <tr
                key={person.board_member_id}
                className="border-b border-border-subtle last:border-b-0"
                data-testid={`return-person-${person.board_member_id}`}
              >
                <td className="py-12 pr-16 align-top">
                  <PersonCell orgRef={orgRef} copy={copy} person={person} canAct={canAct} />
                </td>
                <td className={`py-12 pr-16 align-top tabular-nums ${PLAIN}`}>
                  <OpenedCell copy={copy} person={person} />
                </td>
                <td className={`py-12 pr-16 align-top tabular-nums ${PLAIN}`}>
                  {readCell(person) ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* A phone: each person stacked, each fact with its column's word. */}
        <ul className="flex flex-col md:hidden" data-testid="return-people-list">
          {copy.people.map((person) => (
            <li
              key={person.board_member_id}
              className="flex flex-col gap-4 border-t border-border-subtle py-12 first:border-t-0 first:pt-0"
            >
              <PersonCell orgRef={orgRef} copy={copy} person={person} canAct={canAct} second />
              <span className={PLAIN}>
                {person.opened_at === null && person.printed_copy !== null ? null : (
                  <span className="text-text-tertiary">{w.colOpened}: </span>
                )}
                <OpenedCell copy={copy} person={person} />
              </span>
              <span className={PLAIN}>
                <span className="text-text-tertiary">{w.colRecordedReading}: </span>
                {readCell(person) ?? '—'}
              </span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function PersonCell({
  orgRef,
  copy,
  person,
  canAct,
  second = false,
}: {
  orgRef: string;
  copy: ReturnCopy;
  person: ReturnPerson;
  canAct: boolean;
  /** The phone's stacked copy of the row: the same act, without a second test id. */
  second?: boolean;
}) {
  const facts = personFacts(person);
  return (
    <span className="flex min-w-0 flex-col gap-2">
      <span className={STRONG}>{personName(person)}</span>
      {facts.length > 0 ? <span className={QUIET}>{facts.join(' · ')}</span> : null}
      {canAct && person.can_email_again ? (
        <EmailAgain orgRef={orgRef} copy={copy} person={person} second={second} />
      ) : null}
    </span>
  );
}

function OpenedCell({ copy, person }: { copy: ReturnCopy; person: ReturnPerson }) {
  const printed =
    person.printed_copy?.given_on === null || person.printed_copy === null
      ? null
      : w.printedCopyGiven(shortDay(person.printed_copy.given_on));
  if (person.opened_at === null) return <>{printed ?? '—'}</>;
  const opened = openedWords(person.opened_at, copy.provided_on);
  // One fact stays inline, so a phone keeps it on the line its column's word starts (frame p7).
  if (printed === null) return <>{opened}</>;
  return (
    <span className="flex flex-col gap-2">
      <span>{opened}</span>
      <span className={QUIET}>{printed}</span>
    </span>
  );
}

function EmailAgain({
  orgRef,
  copy,
  person,
  second,
}: {
  orgRef: string;
  copy: ReturnCopy;
  person: ReturnPerson;
  second: boolean;
}) {
  const operations = useBoardOperations();
  const settle = useSettleReturnAct(orgRef);
  const send = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerEmailBoardReturnAgain({
        path: {
          org_ref: orgRef,
          record_id: copy.record_id,
          board_member_id: person.board_member_id,
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      await settle(data);
      // The act answers the receipts read: the person's mail as it stands now says what happened.
      const now = decodeCopy(data)?.people.find(
        (row) => row.board_member_id === person.board_member_id,
      );
      if (now?.email?.sent === true) toast.success(w.emailAgainSent(personName(person)));
    },
    onError: (caught) => toast.error(servedSentence(caught, w.emailAgainFailed)),
  });
  return (
    <button
      type="button"
      className="self-start font-sans text-s13 font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:text-text-tertiary"
      disabled={send.isPending}
      onClick={() => send.mutate()}
      data-testid={second ? undefined : `return-email-again-${person.board_member_id}`}
    >
      {w.emailAgain}
    </button>
  );
}

// ── The copy (frame p7's side card) ──────────────────────────────────────────────────────────────

function CopyCard({ orgRef, copy }: { orgRef: string; copy: ReturnCopy }) {
  const operations = useBoardOperations();
  const open = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerGetBoardReturnCopyUrl({
        path: { org_ref: orgRef, record_id: copy.record_id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => {
      window.open(link.download_url, '_blank', 'noopener,noreferrer');
    },
    onError: (caught) => toast.error(servedSentence(caught, w.openFileFailed)),
  });
  return (
    <Card testId="return-copy">
      <CardBody className="flex flex-col gap-12">
        <PartLabel>{w.theCopy}</PartLabel>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="self-start text-left font-sans text-s15 font-w600 tracking-givmo text-text-strong outline-none transition-colors [overflow-wrap:anywhere] hover:text-brand-700 focus-visible:underline"
            onClick={() => open.mutate()}
            data-testid="return-copy-open"
          >
            {copy.file.file_name}
          </button>
          <span className={QUIET}>
            {w.fileFacts(copy.file.page_count, fileSize(copy.file.byte_size))}
          </span>
        </div>
        {copy.provided_by.display_name !== null ? (
          <p className={PLAIN}>{w.providedBy(copy.provided_by.display_name)}</p>
        ) : null}
        {copy.file.sha256.length > 0 ? (
          <div className="flex flex-col gap-6">
            <span className={QUIET}>{w.fingerprintLead}</span>
            {/* The WHOLE fingerprint (CBG-UX4): a shortened one cannot be compared. */}
            <Fingerprint value={copy.file.sha256} testId="return-copy-fingerprint" />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── The quiet acts and the copy's place in its year ──────────────────────────────────────────────

function QuietActs({
  copy,
  canAct,
  onNewer,
  onWithdraw,
}: {
  copy: ReturnCopy;
  canAct: boolean;
  onNewer: () => void;
  onWithdraw: () => void;
}) {
  const newer = copy.followed_by_record_id;
  const earlier = copy.follows_record_id;
  return (
    <div className="flex flex-col gap-10 px-4" data-testid="return-quiet-acts">
      {newer !== null ? (
        <p className={QUIET} data-testid="return-newer-copy">
          {w.newerCopyProvided}{' '}
          <Link
            to="/form-990/returns/$recordId"
            params={{ recordId: newer }}
            className="font-w600 text-brand-600 hover:text-brand-700"
          >
            {w.openNewerCopy}
          </Link>
        </p>
      ) : null}
      {earlier !== null ? (
        <p className={QUIET} data-testid="return-earlier-copy">
          {w.followsEarlierCopy}{' '}
          <Link
            to="/form-990/returns/$recordId"
            params={{ recordId: earlier }}
            className="font-w600 text-brand-600 hover:text-brand-700"
          >
            {w.openEarlierCopy}
          </Link>
        </p>
      ) : null}
      {canAct && copy.can_provide_newer_copy ? (
        <button
          type="button"
          className={`self-start ${LINK}`}
          onClick={onNewer}
          data-testid="return-provide-newer"
        >
          {w.provideNewerCopy}
        </button>
      ) : null}
      {canAct && copy.can_withdraw ? (
        <button
          type="button"
          className={QUIET_LINK}
          onClick={onWithdraw}
          data-testid="return-withdraw"
        >
          {w.withdrawCopy}
        </button>
      ) : null}
    </div>
  );
}
